import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ExternalLink, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { InboxStatus, InboxThreadDetail } from '../../types/inbox';
import type { CurrentUser } from '../../types/user';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/ui/Spinner';
import { Textarea } from '../../components/ui/textarea';
import { CHANNEL_LABEL, formatExactTimestamp } from '../../lib/inboxView';
import { formatRelativeTime } from '../../lib/relativeTime';
import { isClaimedByOther } from '../../lib/inboxClaim';
import { MessageAttachments } from '../../components/social/MessageAttachments';
import {
  assignThread,
  addThreadNote,
  deleteReviewReply,
  replyToReview,
  setThreadPriority,
  setThreadStatus,
} from './api';
import {
  CollisionBanner,
  DetailHeader,
  NotesPanel,
  NotesToggle,
  PriorityToggle,
  StarRating,
  WorkflowSelects,
  useComposerClaim,
} from './threadDetailShared';
import { SuggestReplyPanel } from './SuggestReplyPanel';
import type { AccountExternalLinks } from '../../types/account';

// Community Inbox Phase 2 — review detail: the full review (stars + reviewer +
// text), our reply (or a reply composer), delete-reply, assign/status/notes,
// and a "View on <platform>" link.

export function ReviewDetailBody({
  detail,
  me,
  users,
  accountNameById,
  accountLinksById,
  onChanged,
}: {
  detail: InboxThreadDetail;
  me: CurrentUser;
  users: CurrentUser[];
  accountNameById: Map<string, string>;
  accountLinksById: Map<string, AccountExternalLinks>;
  onChanged: () => void;
}) {
  const { thread, messages, notes } = detail;
  const body = messages.find((m) => m.direction === 'incoming') ?? messages[0] ?? null;
  const reply = messages.find((m) => m.direction === 'outgoing') ?? null;

  const [draft, setDraft] = useState('');
  const [notesOpen, setNotesOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const claim = useComposerClaim(thread.id);

  const claimedByOther = isClaimedByOther(thread, me.uid, Date.now());
  const claimHolder = claimedByOther ? users.find((u) => u.uid === thread.claimedByUid) : null;

  const replyM = useMutation({
    mutationFn: () => replyToReview(thread.id, draft.trim()),
    onSuccess: () => {
      setDraft('');
      claim.release();
      toast.success('Reply posted.');
      onChanged();
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const deleteReply = useMutation({
    mutationFn: () => deleteReviewReply(thread.id),
    onSuccess: () => {
      toast.success('Reply deleted.');
      onChanged();
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const status = useMutation({
    mutationFn: (next: InboxStatus) => setThreadStatus(thread.id, next),
    onSuccess: onChanged,
    onError: (err: Error) => toast.error(err.message),
  });
  const assign = useMutation({
    mutationFn: (uid: string | null) => assignThread(thread.id, uid),
    onSuccess: onChanged,
    onError: (err: Error) => toast.error(err.message),
  });
  const priority = useMutation({
    mutationFn: (next: boolean) => setThreadPriority(thread.id, next),
    onSuccess: onChanged,
    onError: (err: Error) => toast.error(err.message),
  });
  const note = useMutation({
    mutationFn: () => addThreadNote(thread.id, noteDraft.trim()),
    onSuccess: () => {
      setNoteDraft('');
      onChanged();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200">
      <DetailHeader
        thread={thread}
        accountNameById={accountNameById}
        accountLinks={accountLinksById.get(thread.kyckAccountId)}
        onResolve={() => status.mutate('resolved')}
        resolving={status.isPending}
        extra={
          <>
            <StarRating rating={thread.rating} />
            {thread.hasReply ? (
              <Badge variant="outline" className="border-kyck-200 bg-kyck-50 text-kyck-700">
                Replied
              </Badge>
            ) : (
              <Badge variant="outline">Needs reply</Badge>
            )}
            <PriorityToggle
              priority={thread.priority === true}
              disabled={priority.isPending}
              onToggle={() => priority.mutate(thread.priority !== true)}
            />
          </>
        }
      >
        <WorkflowSelects
          thread={thread}
          users={users}
          onStatus={(s) => status.mutate(s)}
          onAssign={(uid) => assign.mutate(uid)}
        />
      </DetailHeader>

      {claimedByOther && (
        <CollisionBanner label={(claimHolder?.displayName || claimHolder?.email) ?? 'Another agent'} />
      )}

      <div className="scroll-subtle min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
        {/* The review */}
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="flex items-center gap-2">
            <StarRating rating={thread.rating} />
            <span className="text-sm font-semibold text-slate-800">{thread.author.name}</span>
            {body && (
              <span
                className="ml-auto text-xs text-slate-400"
                title={formatRelativeTime(body.createdAt)}
              >
                {formatExactTimestamp(body.createdAt)}
              </span>
            )}
          </div>
          <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">
            {body?.text || <span className="italic text-slate-400">No review text.</span>}
          </p>
          {/* Review photos (GBP only, undocumented reviewMediaItems passthrough).
              Additive context — renders nothing when the review has none, which
              is most of them. Reuses the shared thumbnail + lightbox. */}
          {body && <MessageAttachments attachments={body.attachments} />}
          {thread.permalink && (
            <a
              href={thread.permalink}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center gap-1 text-xs text-kyck-700 hover:underline"
            >
              <ExternalLink className="h-3 w-3" />
              View on {CHANNEL_LABEL[thread.channel]}
            </a>
          )}
        </div>

        {/* Our reply, if one exists */}
        {reply && (
          <div className="ml-6 rounded-lg border border-kyck-200 bg-kyck-50/40 p-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-kyck-800">Your reply</span>
              <span className="text-[11px] text-slate-400" title={formatRelativeTime(reply.createdAt)}>
                {formatExactTimestamp(reply.createdAt)}
              </span>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Delete this reply on the platform? This cannot be undone.')) {
                    deleteReply.mutate();
                  }
                }}
                disabled={deleteReply.isPending}
                title="Delete reply"
                className="ml-auto inline-flex items-center gap-1 text-xs text-slate-400 hover:text-red-600"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </button>
            </div>
            <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{reply.text}</p>
          </div>
        )}
      </div>

      {notesOpen && (
        <NotesPanel
          notes={notes}
          draft={noteDraft}
          setDraft={setNoteDraft}
          onAdd={() => note.mutate()}
          adding={note.isPending}
        />
      )}

      {/* Reply composer (only when there's no reply yet) */}
      <div className="shrink-0 border-t border-slate-200 bg-white px-4 py-3">
        {!reply ? (
          <>
            {/* AI drafts. Tone scales with the star rating server-side — a 1★
                reply is calm and moves offline, a 5★ one is warm and brief. */}
            <SuggestReplyPanel
              className="mb-2"
              threadId={thread.id}
              messageId={body?.id}
              onUse={setDraft}
            />
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onFocus={claim.onFocus}
              placeholder={`Write a reply to ${thread.author.name}…`}
              rows={2}
            />
            <div className="mt-2 flex items-center gap-2">
              <NotesToggle count={notes.length} onToggle={() => setNotesOpen((o) => !o)} />
              <span className="flex-1" />
              <Button
                size="sm"
                onClick={() => {
                  if (draft.trim() === '' || replyM.isPending) return;
                  if (
                    claimedByOther &&
                    !window.confirm(
                      `${claimHolder?.displayName ?? 'Another agent'} is already replying. Send anyway?`,
                    )
                  ) {
                    return;
                  }
                  replyM.mutate();
                }}
                disabled={draft.trim() === '' || replyM.isPending}
              >
                {replyM.isPending ? <Spinner className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
                Reply
              </Button>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">
              This review has a reply. Delete it above to reply again.
            </span>
            <span className="flex-1" />
            <NotesToggle count={notes.length} onToggle={() => setNotesOpen((o) => !o)} />
          </div>
        )}
      </div>
    </div>
  );
}
